/**
 * SocialCalendarView — social-post calendar (Hono-backed).
 *
 * S-069 / FEAT-006 (Phase 8). Originally landed at `/calendar` under
 * the file name `CalendarView.tsx`. Renamed and rerouted to `/social`
 * in S-080 (Phase 8 fix) once we realized the legacy `/calendar`
 * route was the article-publication calendar, not a social grid.
 * The new article-publication calendar lives in `CalendarView.tsx`
 * at the same `/calendar` URL. This file is the social grid that
 * was originally written for Phase 8 — moved, not rewritten.
 *
 * The model unit here is a row in the `social_calendar` table (a
 * scheduled social post produced by the amplifier pipeline). Cards
 * are addressed by the post id, not the parent piece id.
 *
 * What it does:
 *   - No workspace selector of its own — FEAT-087 moved the picker
 *     so cross-workspace mutations land under the right RLS scope
 *     (same Phase 5 pattern used in PublicationsView / TeamView).
 *   - Month grid (default) or Day view. Anchor + prev/next/today +
 *     "Day" toggle preserve the legacy view's UX shape.
 *   - GET /social-calendar?agencyclient_id=N&from=ISO&to=ISO — the
 *     route is authoritative (verbs and filter shape confirmed by
 *     reading apps/api/src/routes/social-calendar.ts). Posts are
 *     grouped client-side by the local-tz date of `scheduled_at`.
 *   - Status filter chips (all/draft/approved/scheduled/published/
 *     cancelled). Posts whose scheduled_at is null (drafts not yet
 *     scheduled) are rendered in a "Backlog" strip above the grid so
 *     they don't disappear from view.
 *   - Per-card display:
 *       Amplifier name + platform glyph (LinkedIn/X/Instagram/
 *         Threads/Substack — inline SVG, no new icon library)
 *       Client name (the piece's agencyclient — surfaced separately
 *         from amplifier per the project memory: Client is the
 *         content owner, Amplifier is the posting identity; these
 *         are independent axes)
 *       Scheduled HH:MM (local time)
 *       Status pill (color-coded)
 *   - Card click → side drawer (S-070) with full post details and
 *     editable scheduled_at / body / image. Approve / Schedule /
 *     Cancel actions wire to the FEAT-004 action endpoints.
 *
 * State machine the drawer respects (server-enforced, confirmed
 * against the route file):
 *     draft → approved → scheduled
 *   plus terminal: published (set by Blotato dispatcher), cancelled
 *   (set by DELETE — there is NO /cancel endpoint; the route uses
 *   DELETE /social-calendar/:id and the schema's status enum does
 *   accept "cancelled", but DELETE today hard-removes the row.
 *   To preserve the row for audit and re-render with the cancelled
 *   pill, we PATCH the row's status... except PATCH only accepts
 *   body / image_file_id / scheduled_at per the route's zod schema.
 *   Resolution for v1: "Cancel" calls DELETE which removes the row
 *   from the calendar entirely. The browser-smoke note flags this
 *   so a follow-up story can extend PATCH or add a /cancel route.)
 *
 * Image handling:
 *   - The schema field is `image_file_id` (uuid, R2 reference) — not
 *     image_url. The drawer reuses the BrandIdentityEditor LogoUploader
 *     pattern: POST /files (multipart) returns { id }, we store the
 *     id, and the preview hits `${VITE_API_URL}/assets/<id>`.
 *
 * Concurrent edit:
 *   - 409 from POST /approve or POST /schedule indicates the row's
 *     status changed under us. We surface "This post changed since
 *     you opened it — refresh to continue" inline and let the user
 *     close + reopen the drawer.
 *
 * Notes for browser smoke:
 *   - The legacy CalendarView previously rendered piece-level rows
 *     keyed by article id. Anything elsewhere in the SPA that linked
 *     to /calendar still works (the route stays the same); but any
 *     deep-link to a specific calendar item (none today) would need
 *     updating. The legacy `@/lib/queries` helpers and `@/lib/ui-state`
 *     remain in place for KanbanView; this file no longer imports them.
 */

import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
// Consolidation (option 3): shared admin drawer shell. This view previously
// hand-rolled the drawer markup inline; it now uses the canonical DrawerShell.
import { DrawerShell } from "@/components/SettingsModalShell";
// FEAT-092 / ADR-022 — the fields and actions on a social_calendar row live in
// one component, shared with the piece screen's Amplifications document. This
// view supplies the drawer around it, not a second copy of the rules.
import { SocialPostEditor } from "@/components/SocialPostEditor";
import { STATUS_LABEL, socialStatusKey, socialStatusLabel } from "@/lib/social-status";
import { usePieceLookup } from "@/lib/piece-lookup";
import { ApiError, useApiQuery } from "@/lib/api";
import { AccountAttentionBanner } from "@/components/AccountAttentionBanner";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { useWorkspaceContext } from "@/lib/workspace-context";
import { amplifierDisplayName, useAmplifierLookup } from "@/lib/amplifier-lookup";
import { socialAccountDisplayName, useSocialAccountLookup } from "@/lib/social-account-lookup";
import { useLinkedinArticleLabel } from "@/lib/linkedin-article-label";
import { workspaceBorderStyle } from "@/lib/workspace-color";
import { localDateIso, addDays } from "@/lib/calendar-range";
import { groupPostsByPiece } from "@/lib/social-grouping";

// ---------- API row shapes ----------

interface SocialCalendarRow {
  id: string; // bigserial serialized as string by the API
  tenant_id: number;
  agencyclient_id: number;
  piece_id: number | null;
  // FEAT-095 / S-401 retirement — a row created after the migration carries
  // account_id instead; amplifier_id survives only on pre-migration rows.
  amplifier_id: number | null;
  account_id: number | null;
  platform: string;
  body: string;
  image_file_id: string | null;
  status: string;
  scheduled_at: string | null;
  approved_by_user_id: string | null;
  approved_at: string | null;
  dispatch_post_id: string | null;
  published_url: string | null;
  created_at: string;
  updated_at: string;
}

interface SocialCalendarListResponse {
  data: SocialCalendarRow[];
  meta: { total: number; limit: number; offset: number };
}

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

// ---------- Status semantics ----------

// FEAT-062 review — "posted"/"failed" are real social_calendar.status CHECK
// values (posted: written by POST /:id/mark-posted, the manual-confirmation
// action; failed: reserved for a future dispatcher) that this view never
// accounted for. Before this fix, a row marked posted vanished from every
// list here — not in ALL_STATUSES means not in the default filter set, and
// there was no chip to re-enable seeing it.
const STATUS_PILL_CLASS: Record<string, string> = {
  draft: "pill-muted",
  approved: "pill-info",
  scheduled: "pill-info",
  published: "pill-ok",
  posted: "pill-ok",
  cancelled: "pill-warn",
  failed: "pill-warn",
};

const ALL_STATUSES = [
  "draft",
  "approved",
  "scheduled",
  "published",
  "posted",
  "cancelled",
  "failed",
] as const;
type StatusKey = (typeof ALL_STATUSES)[number];

// ---------- Platform glyphs (inline SVG — no new icon library) ----------

function PlatformGlyph({ platform }: { platform: string }) {
  const p = platform.toLowerCase();
  // Single common shape per platform — kept small and monochrome so the
  // card can color it via currentColor. Not pixel-perfect brand marks;
  // good enough for an at-a-glance row identifier.
  // FEAT-086 / S-388 — `linkedin_article` is the post that fills LinkedIn's
  // "Create post modal" when a long-form article is published there. Same
  // mark, inverted: it is unmistakably LinkedIn, and unmistakably not the
  // regular blue amplification beside it. The two behave completely
  // differently and nothing else on the card says so.
  if (p === "linkedin" || p === "linkedin_article") {
    return (
      <svg
        className="cal-card-glyph"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        aria-label={p === "linkedin_article" ? "LinkedIn article post" : "LinkedIn"}
      >
        <path
          fill="currentColor"
          d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8h4.56v14H.22V8zm7.46 0h4.36v1.93h.06c.61-1.15 2.1-2.36 4.33-2.36 4.63 0 5.48 3.05 5.48 7.02V22h-4.56v-6.2c0-1.48-.03-3.39-2.07-3.39-2.07 0-2.39 1.62-2.39 3.29V22H7.68V8z"
        />
      </svg>
    );
  }
  if (p === "x" || p === "twitter") {
    return (
      <svg
        className="cal-card-glyph"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        aria-label="X"
      >
        <path
          fill="currentColor"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"
        />
      </svg>
    );
  }
  if (p === "instagram") {
    return (
      <svg
        className="cal-card-glyph"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        aria-label="Instagram"
      >
        <path
          fill="currentColor"
          d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07c-1.28.06-2.15.26-2.91.55-.79.31-1.46.72-2.13 1.38-.66.66-1.07 1.34-1.38 2.13-.3.76-.5 1.63-.55 2.91C0 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.55 2.91.31.79.72 1.46 1.38 2.13.66.66 1.34 1.07 2.13 1.38.76.3 1.63.5 2.91.55C8.33 24 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.55.79-.31 1.46-.72 2.13-1.38.66-.66 1.07-1.34 1.38-2.13.3-.76.5-1.63.55-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.55-2.91-.31-.79-.72-1.46-1.38-2.13C21.34 1.06 20.66.65 19.87.34c-.76-.3-1.63-.5-2.91-.55C15.67 0 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 12 18.16 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 12 8a4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"
        />
      </svg>
    );
  }
  if (p === "threads") {
    return (
      <svg
        className="cal-card-glyph"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        aria-label="Threads"
      >
        <path
          fill="currentColor"
          d="M17.62 11.13c-.09-.04-.19-.09-.29-.13-.16-2.96-1.78-4.66-4.49-4.68h-.04c-1.62 0-2.97.69-3.81 1.95l1.5 1.03c.62-.94 1.6-1.14 2.31-1.14h.03c.89.01 1.55.27 1.97.77.31.36.51.86.61 1.48-.74-.13-1.54-.17-2.4-.12-2.41.14-3.96 1.55-3.86 3.5.05.99.54 1.84 1.39 2.39.71.46 1.63.69 2.59.64 1.27-.07 2.27-.55 2.96-1.44.53-.67.86-1.55 1-2.65.59.36.95.83 1.16 1.4.34.97.36 2.56-.94 3.86-1.14 1.14-2.51 1.63-4.6 1.65-2.31-.02-4.07-.76-5.22-2.21C5.43 16.04 4.87 14.2 4.85 12c.02-2.2.58-4.04 1.66-5.46C7.66 5.09 9.42 4.35 11.73 4.33c2.33.02 4.12.76 5.31 2.22.59.71 1.03 1.6 1.31 2.65l1.81-.48c-.34-1.28-.88-2.39-1.61-3.27C16.97 3.61 14.71 2.65 11.74 2.62h-.01c-2.96.02-5.18.98-6.62 2.86C3.78 7.21 3.1 9.41 3.07 12v.01c.03 2.59.7 4.78 2.04 6.5 1.44 1.88 3.66 2.84 6.62 2.86h.01c2.63-.02 4.49-.71 6.02-2.24 2-2 1.94-4.5 1.28-6.03-.47-1.1-1.37-1.99-2.61-2.59zm-4.93 4.13c-1.06.06-2.16-.42-2.21-1.45-.04-.76.54-1.61 2.27-1.71.2-.01.39-.02.58-.02.63 0 1.21.06 1.74.18-.2 2.49-1.37 2.94-2.38 3z"
        />
      </svg>
    );
  }
  if (p === "substack") {
    return (
      <svg
        className="cal-card-glyph"
        viewBox="0 0 24 24"
        width="14"
        height="14"
        aria-label="Substack"
      >
        <path
          fill="currentColor"
          d="M3 4h18v3H3V4zm0 5h18v3H3V9zm0 5h18v8l-9-4.5L3 22v-8z"
        />
      </svg>
    );
  }
  // Fallback: short text glyph
  return (
    <span className="cal-card-glyph cal-card-glyph-text">
      {platform.slice(0, 3).toUpperCase()}
    </span>
  );
}

// ---------- Date helpers ----------

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function monthRange(anchor: Date): { from: string; to: string } {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const from = new Date(y, m, 1, 0, 0, 0, 0);
  const to = new Date(y, m + 1, 1, 0, 0, 0, 0); // exclusive
  return { from: from.toISOString(), to: to.toISOString() };
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function fmtHm(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "—";
  }
}

function fmtFullDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// ---------- Top-level view ----------

// BUG-068 — workspace selection now lives in the shared WorkspaceProvider
// (mounted once in Shell.tsx) instead of this view's own sessionStorage-
// backed state, so switching workspace here carries over to
// Kanban/Calendar and vice versa. This also switches the workspace list
// itself from GET /agencyclients (every workspace in the tenant) to GET
// /agencyclients/me (only workspaces the caller is a member of) — the
// same membership-scoped list Kanban/Calendar already used (FEAT-021/
// S-180). This view was the one holdout still using the broader
// endpoint; aligning it closes that gap as a side effect.
export function SocialCalendarView() {
  const {
    workspaces,
    isLoading: wsLoading,
    error: wsError,
    selectedId,
  } = useWorkspaceContext();

  if (wsLoading) {
    return (
      <main className="main">
        <div className="state-loading">Loading calendar…</div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main">
        <div className="state-error">
          {wsError instanceof ApiError && wsError.status === 403
            ? "You don't have permission to view the calendar."
            : (wsError.message ?? "Could not load workspaces.")}
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main">
        <div className="cal-wrap">
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings to start scheduling
              social posts.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <CalendarBody
      workspaces={workspaces}
      selectedId={selectedId}
    />
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedId: number | null;
}

/** S-378 — Social's own ranges. Queue is not a date range: it is the piece
 *  view, which is the one the handoff argues for. */
type SocialMode = "queue" | "day" | "month";
const SOCIAL_MODES: ReadonlyArray<{ id: SocialMode; label: string }> = [
  { id: "queue", label: "Queue" },
  { id: "day", label: "Day" },
  { id: "month", label: "Month" },
];

function CalendarBody({ workspaces, selectedId }: BodyProps) {
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? workspaces[0],
    [workspaces, selectedId]
  );
  const activeId = activeWs.id;
  const allWorkspaces = selectedId === 0;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  // BUG-068 — skip this when "All workspaces" is selected: activeId falls
  // back to workspaces[0] in that mode (it isn't a real single-workspace
  // selection), so syncing it would silently repoint the real backend
  // active workspace to workspaces[0] every time All Workspaces is chosen.
  useEnsureActiveWorkspace(allWorkspaces ? undefined : activeId);
  const [mode, setMode] = useState<SocialMode>("month");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [statusFilters, setStatusFilters] = useState<Set<StatusKey>>(
    () => new Set(ALL_STATUSES)
  );
  const [openId, setOpenId] = useState<string | null>(null);



  // The route accepts ?from=&to= as ISO dates. We always request the
  // active anchor's month so the day-view can jump across months
  // without an extra fetch; the day view itself just slices the
  // already-grouped map.
  const range = useMemo(() => monthRange(anchor), [anchor]);

  const { data: wsData, isLoading: wsLoading, error: wsError, refetch: wsRefetch } =
    useApiQuery<SocialCalendarListResponse>(
      ["social-calendar", activeId, anchor.getFullYear(), anchor.getMonth()],
      `/social-calendar?agencyclient_id=${activeId}&from=${encodeURIComponent(
        range.from
      )}&to=${encodeURIComponent(range.to)}`,
      {
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        enabled: !allWorkspaces,
      }
    );

  // All-workspaces overview for the social calendar.
  const { data: overviewData, isLoading: overviewLoading, error: overviewError, refetch: overviewRefetch } =
    useApiQuery<SocialCalendarListResponse>(
      ["overview-calendar", "all", anchor.getFullYear(), anchor.getMonth()],
      `/overview/calendar?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      {
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        enabled: allWorkspaces,
      }
    );

  const data = allWorkspaces ? overviewData : wsData;
  const isLoading = allWorkspaces ? overviewLoading : wsLoading;
  const error = allWorkspaces ? overviewError : wsError;
  const refetch = allWorkspaces ? overviewRefetch : wsRefetch;

  // Workspace color border for cards from other workspaces when allWorkspaces is on.
  const getCardStyle = allWorkspaces
    ? (row: SocialCalendarRow): CSSProperties | undefined =>
        row.agencyclient_id !== activeId
          ? workspaceBorderStyle(row.agencyclient_id)
          : undefined
    : undefined;

  const allRows = data?.data ?? [];

  // S-378 — the queue groups by the piece each post amplifies, so it needs
  // titles. One workspace list query, rather than the per-piece lookup the
  // drawer uses: a queue of thirty posts would otherwise fire thirty.
  const { data: piecesResp } = useApiQuery<{ data: { id: number; title: string | null; slug: string }[] }>(
    ["pieces", activeId, "social-titles"],
    `/pieces?agencyclient_id=${activeId}&limit=200`,
    { enabled: mode === "queue" }
  );
  const pieceTitle = (id: number): string | undefined => {
    const p = piecesResp?.data?.find((x) => x.id === id);
    return p ? (p.title || p.slug) : undefined;
  };
  const filteredRows = useMemo(
    () => allRows.filter((r) => statusFilters.has(socialStatusKey(r) as StatusKey)),
    [allRows, statusFilters]
  );

  // Group by local-tz YYYY-MM-DD. Posts with null scheduled_at land in
  // a separate "backlog" bucket so unscheduled drafts stay visible.
  const grouped = useMemo(() => {
    const byDate = new Map<string, SocialCalendarRow[]>();
    const backlog: SocialCalendarRow[] = [];
    for (const r of filteredRows) {
      if (!r.scheduled_at) {
        backlog.push(r);
        continue;
      }
      const iso = localDateIso(new Date(r.scheduled_at));
      const list = byDate.get(iso) ?? [];
      list.push(r);
      byDate.set(iso, list);
    }
    // Sort each day by time ascending.
    for (const list of byDate.values()) {
      list.sort((a, b) => {
        const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
        const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
        return ta - tb;
      });
    }
    return { byDate, backlog };
  }, [filteredRows]);

  function toggleStatus(s: StatusKey) {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function toggleAll() {
    setStatusFilters((prev) =>
      prev.size === ALL_STATUSES.length ? new Set() : new Set(ALL_STATUSES)
    );
  }

  const today = new Date();
  const todayIso = localDateIso(today);

  const openRow = openId ? allRows.find((r) => r.id === openId) ?? null : null;

  return (
    <main className="main">
      <div className="cal-wrap">
        <div className="cal-head">
          <div>
            <h1 className="view-title">Social Calendar</h1>
            <p className="view-sub">
              {mode === "queue"
                ? `${filteredRows.length} ${
                    filteredRows.length === 1 ? "post" : "posts"
                  }, by the piece each one amplifies`
                : mode === "month"
                ? `${monthLabel(anchor)} · ${allRows.length} posts (${filteredRows.length} visible)`
                : `${anchor.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })} · ${
                    grouped.byDate.get(localDateIso(anchor))?.length ?? 0
                  } posts`}
            </p>
          </div>
          <div className="cal-head-r">
            <div className="cal-modes" role="tablist" aria-label="Social range">
              {SOCIAL_MODES.map((sm) => (
                <button
                  key={sm.id}
                  role="tab"
                  aria-selected={mode === sm.id}
                  className={`cal-mode ${mode === sm.id ? "is-active" : ""}`}
                  onClick={() => setMode(sm.id)}
                >
                  {sm.label}
                </button>
              ))}
            </div>
            <div className="cal-nav" hidden={mode === "queue"}>
              <button
                className="cal-nav-btn"
                onClick={() => setAnchor(stepAnchor(anchor, mode, -1))}
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                className="cal-nav-today"
                onClick={() => setAnchor(new Date())}
              >
                Today
              </button>
              <button
                className="cal-nav-btn"
                onClick={() => setAnchor(stepAnchor(anchor, mode, 1))}
                aria-label="Next"
              >
                ›
              </button>
            </div>
          </div>
        </div>
        {/* FEAT-107 / S-435 — a broken account, and one action for its posts. */}
        {!allWorkspaces && <AccountAttentionBanner />}

        <div className="cal-status-chips">
          <button
            type="button"
            className={`cal-chip ${
              statusFilters.size === ALL_STATUSES.length ? "is-active" : ""
            }`}
            onClick={toggleAll}
          >
            All
          </button>
          {ALL_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`cal-chip ${statusFilters.has(s) ? "is-active" : ""}`}
              onClick={() => toggleStatus(s)}
            >
              <span className={`pill ${STATUS_PILL_CLASS[s] ?? "pill-muted"}`}>
                {STATUS_LABEL[s] ?? s}
              </span>
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="state-loading">Loading social posts…</div>
        )}

        {error && !isLoading && (
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view this workspace's calendar."
              : (error.message ?? "Could not load social calendar.")}
          </div>
        )}

        {!isLoading && !error && allRows.length === 0 && (
          <div className="settings-empty" style={{ marginTop: 16 }}>
            <h2>No social posts scheduled</h2>
            <p>
              Pieces published with amplification rules attached will fill
              this calendar automatically. You can also schedule ad-hoc
              posts via the amplifier UI.
            </p>
          </div>
        )}

        {!isLoading && !error && allRows.length > 0 && grouped.backlog.length > 0 && (
          <BacklogStrip
            rows={grouped.backlog}
            workspaceId={activeId}
            onOpen={(id) => setOpenId(id)}
            getCardStyle={getCardStyle}
          />
        )}

        {!isLoading && !error && mode === "month" && allRows.length > 0 && (
          <MonthGrid
            anchor={anchor}
            byDate={grouped.byDate}
            todayIso={todayIso}
            workspaceId={activeId}
            onOpen={(id) => setOpenId(id)}
            getCardStyle={getCardStyle}
          />
        )}

        {!isLoading && !error && mode === "queue" && allRows.length > 0 && (
          <div className="social-queue">
            {groupPostsByPiece(filteredRows, pieceTitle).map((group) => (
              <section className="social-group" key={group.pieceId ?? "none"}>
                <header className="social-group-head">
                  <span className="social-group-title">{group.title}</span>
                  <span className="social-group-rule" />
                  <span className="social-group-count">
                    {group.rows.length}{" "}
                    {group.rows.length === 1 ? "post" : "posts"}
                  </span>
                </header>
                <div className="social-group-rows">
                  {group.rows.map((row) => (
                    <PostCard
                      key={row.id}
                      row={row}
                      workspaceId={activeId}
                      large
                      onOpen={(id) => setOpenId(id)}
                      extraStyle={getCardStyle?.(row)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {!isLoading && !error && mode === "day" && allRows.length > 0 && (
          <DayView
            anchor={anchor}
            rows={grouped.byDate.get(localDateIso(anchor)) ?? []}
            isToday={localDateIso(anchor) === todayIso}
            workspaceId={activeId}
            onOpen={(id) => setOpenId(id)}
            getCardStyle={getCardStyle}
          />
        )}
      </div>

      {openRow && (
        <PostDrawer
          row={openRow}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            void refetch();
          }}
          onRemoved={() => {
            setOpenId(null);
            void refetch();
          }}
        />
      )}
    </main>
  );
}

// ---------- Month grid ----------

interface MonthGridProps {
  anchor: Date;
  byDate: Map<string, SocialCalendarRow[]>;
  todayIso: string;
  workspaceId: number;
  onOpen: (id: string) => void;
  getCardStyle?: (row: SocialCalendarRow) => CSSProperties | undefined;
}

function MonthGrid({ anchor, byDate, todayIso, workspaceId, onOpen, getCardStyle }: MonthGridProps) {
  const cells = useMemo(() => {
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    const first = new Date(y, m, 1);
    const firstDow = first.getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const list: Array<{
      key: string;
      pad?: boolean;
      date?: Date;
      iso?: string;
    }> = [];
    for (let i = 0; i < firstDow; i++) list.push({ key: `pad-${i}`, pad: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(y, m, d);
      const iso = localDateIso(date);
      list.push({ key: iso, date, iso });
    }
    while (list.length % 7 !== 0) {
      list.push({ key: `pad-end-${list.length}`, pad: true });
    }
    return list;
  }, [anchor]);

  return (
    <div className="cal-grid cal-grid-month">
      {DOW.map((d) => (
        <div key={d} className="cal-dow">
          {d}
        </div>
      ))}
      {cells.map((c) => {
        if (c.pad) return <div key={c.key} className="cal-cell cal-pad" />;
        const isToday = c.iso === todayIso;
        const items = byDate.get(c.iso!) ?? [];
        return (
          <div key={c.key} className={`cal-cell ${isToday ? "is-today" : ""}`}>
            <div className="cal-day">
              {c.date!.getDate()}
              {isToday && <span className="cal-today">today</span>}
            </div>
            <div className="cal-items">
              {items.slice(0, 3).map((row) => (
                <PostCard
                  key={row.id}
                  row={row}
                  workspaceId={workspaceId}
                  onOpen={onOpen}
                  extraStyle={getCardStyle?.(row)}
                />
              ))}
              {items.length > 3 && (
                <span className="cal-overflow">+{items.length - 3} more</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Day view ----------

interface DayViewProps {
  anchor: Date;
  rows: SocialCalendarRow[];
  isToday: boolean;
  workspaceId: number;
  onOpen: (id: string) => void;
  getCardStyle?: (row: SocialCalendarRow) => CSSProperties | undefined;
}

function DayView({ anchor, rows, isToday, workspaceId, onOpen, getCardStyle }: DayViewProps) {
  return (
    <div className="cal-day-wrap">
      <div className="cal-day-head">
        <span className="cal-day-num">{anchor.getDate()}</span>
        <div className="cal-day-meta">
          <span className="cal-day-dow">{DOW[anchor.getDay()]}</span>
          <span className="cal-day-date">
            {anchor.toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        </div>
        {isToday && <span className="cal-today cal-today-lg">today</span>}
      </div>
      <div className="cal-day-body">
        {rows.length === 0 && (
          <div className="cal-day-empty">
            Nothing scheduled for this day.
          </div>
        )}
        {rows.map((row) => (
          <PostCard
            key={row.id}
            row={row}
            workspaceId={workspaceId}
            onOpen={onOpen}
            large
            extraStyle={getCardStyle?.(row)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- Backlog strip (unscheduled drafts) ----------

function BacklogStrip({
  rows,
  workspaceId,
  onOpen,
  getCardStyle,
}: {
  rows: SocialCalendarRow[];
  workspaceId: number;
  onOpen: (id: string) => void;
  getCardStyle?: (row: SocialCalendarRow) => CSSProperties | undefined;
}) {
  return (
    <div className="cal-backlog">
      <div className="cal-backlog-head">
        <strong>Unscheduled</strong>
        <span className="cal-backlog-hint">
          {rows.length} post{rows.length === 1 ? "" : "s"} without a
          scheduled time. Open a card to schedule.
        </span>
      </div>
      <div className="cal-backlog-list">
        {rows.map((row) => (
          <PostCard
            key={row.id}
            row={row}
            workspaceId={workspaceId}
            onOpen={onOpen}
            large
            extraStyle={getCardStyle?.(row)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- Card ----------

interface PostCardProps {
  row: SocialCalendarRow;
  workspaceId: number;
  onOpen: (id: string) => void;
  large?: boolean;
  extraStyle?: CSSProperties;
}

function PostCard({ row, workspaceId, onOpen, large, extraStyle }: PostCardProps) {
  // FEAT-095 / S-401 retirement — only one of these two resolves per row;
  // see the SocialCalendarRow.account_id comment above.
  const amp = useAmplifierLookup(row.account_id == null ? row.amplifier_id : null);
  const account = useSocialAccountLookup(row.agencyclient_id, row.account_id);
  const piece = usePieceLookup(row.piece_id);
  const client = useAgencyclientLookup(row.agencyclient_id);

  const statusKey = socialStatusKey(row) as StatusKey;
  const pillCls = STATUS_PILL_CLASS[statusKey] ?? "pill-muted";
  const time = fmtHm(row.scheduled_at);

  // A LinkedIn article post is published by its newsletter's publish target,
  // never by the account its row happens to name.
  const articleLabel = useLinkedinArticleLabel(row.platform, row.piece_id);
  const ampLabel =
    articleLabel ??
    (row.account_id != null
      ? socialAccountDisplayName(account, row.account_id)
      : amplifierDisplayName(amp, row.amplifier_id ?? 0));
  const clientLabel = client?.name ?? `workspace #${row.agencyclient_id}`;
  const pieceLabel = piece?.title ?? (row.piece_id ? `piece #${row.piece_id}` : "Ad-hoc post");

  // The workspaceId arg is referenced so React doesn't refetch deps
  // for it; in practice it's read upstream for invalidation keying.
  void workspaceId;

  return (
    <button
      type="button"
      className={`cal-card ${large ? "cal-card-lg" : ""}`}
      onClick={() => onOpen(row.id)}
      title={`${ampLabel} · ${PLATFORM_TITLE[row.platform] ?? row.platform} · ${time}`}
      style={extraStyle}
    >
      <span className="cal-card-row cal-card-row-top">
        <span className={`cal-card-platform cal-card-platform-${row.platform.toLowerCase()}`}>
          <PlatformGlyph platform={row.platform} />
          <span className="cal-card-time mono">{time}</span>
        </span>
        <span className={`pill ${pillCls}`}>
          {socialStatusLabel(row)}
        </span>
      </span>
      <span className="cal-card-amp">{ampLabel}</span>
      <span className="cal-card-client">
        <span className="cal-card-client-label">Client</span>
        <span className="cal-card-client-name">{clientLabel}</span>
      </span>
      {large && (
        <span className="cal-card-piece">{pieceLabel}</span>
      )}
    </button>
  );
}

/** Queue has no anchor to move; Day and Month step by their own size. */
function stepAnchor(d: Date, mode: SocialMode, dir: 1 | -1): Date {
  if (mode === "day") return addDays(d, dir);
  const next = new Date(d);
  next.setDate(1);
  next.setMonth(next.getMonth() + dir);
  return next;
}

// ---------- Lookup hooks (cached via TanStack Query) ----------

function useAgencyclientLookup(id: number) {
  const { data } = useApiQuery<Agencyclient>(
    ["agencyclient", id],
    `/agencyclients/${id}`,
    {
      staleTime: 5 * 60_000,
      retry: false,
    }
  );
  return data;
}

// ---------- Drawer (S-070) ----------

interface DrawerProps {
  row: SocialCalendarRow;
  onClose: () => void;
  onChanged: () => void;
  onRemoved: () => void;
}

/** Human names for the tooltip — `linkedin_article` reads as machinery otherwise. */
const PLATFORM_TITLE: Record<string, string> = {
  linkedin: "LinkedIn post",
  linkedin_article: "LinkedIn article post",
  x: "X",
  instagram: "Instagram",
  threads: "Threads",
};

function PostDrawer({ row, onClose, onChanged, onRemoved }: DrawerProps) {
  const amp = useAmplifierLookup(row.account_id == null ? row.amplifier_id : null);
  const account = useSocialAccountLookup(row.agencyclient_id, row.account_id);
  const piece = usePieceLookup(row.piece_id);
  const client = useAgencyclientLookup(row.agencyclient_id);

  // A LinkedIn article post is published by its newsletter's publish target,
  // never by the account its row happens to name.
  const articleLabel = useLinkedinArticleLabel(row.platform, row.piece_id);
  const ampLabel =
    articleLabel ??
    (row.account_id != null
      ? socialAccountDisplayName(account, row.account_id)
      : amplifierDisplayName(amp, row.amplifier_id ?? 0));
  const clientLabel = client?.name ?? `workspace #${row.agencyclient_id}`;
  const pieceLabel = piece?.title ?? (row.piece_id ? `piece #${row.piece_id}` : "Ad-hoc post");

  // Escape key closes the drawer. The fields and actions below are
  // SocialPostEditor's (FEAT-092 / ADR-022) — this component is the drawer
  // chrome around them and nothing else.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <DrawerShell
      wide
      eyebrow="Social calendar"
      title={`Social post #${row.id}`}
      onClose={onClose}
    >
      <nav className="editor-breadcrumb" aria-label="Breadcrumb">
        <span>{clientLabel}</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>{pieceLabel}</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>{ampLabel}</span>
        <span className="editor-breadcrumb-sep">/</span>
        <span>{row.platform}</span>
      </nav>

      <div className="cal-drawer-meta">
        <span className={`pill ${STATUS_PILL_CLASS[row.status] ?? "pill-muted"}`}>
          {socialStatusLabel(row)}
        </span>
        <span className="cal-drawer-meta-item">
          Created {fmtFullDate(row.created_at)}
        </span>
        {row.approved_at && (
          <span className="cal-drawer-meta-item">
            Approved {fmtFullDate(row.approved_at)}
          </span>
        )}
        {row.published_url && (
          <a
            href={row.published_url}
            target="_blank"
            rel="noopener noreferrer"
            className="settings-link"
          >
            View published post ↗
          </a>
        )}
      </div>

      <SocialPostEditor row={row} onChanged={onChanged} onRemoved={onRemoved} />
    </DrawerShell>
  );
}
